import { MultiVoucherDetails } from 'app/src/features/Shopping/Direct/MultiVoucher/components/MultiVoucherDetails'
import { MultiVoucherLeftPanelSide } from 'app/src/features/Shopping/Direct/MultiVoucher/components/MultiVoucherLeftPanelSide'
import { MultiVoucherRightPanelSide } from 'app/src/features/Shopping/Direct/MultiVoucher/components/MultiVoucherRightPanelSide'
import {
  BuildBeneficiaries,
  cardPollingWorker,
  CardPurchaseStatus,
  getCardPurchaseStatus,
  type CardInProgressViewBaseProps,
  type CardPurchaseData,
} from 'app/src/features/Shopping/shared/utils'
import { shoppingApi, type portalConfigurationApi } from 'app/src/services/welfareportal'
import { BackToCatalogButton, ErrorPage, Image, PortalConfirmDialog } from 'app/src/shared/components'
import { formatDateDDMMYYYY } from 'app/src/shared/helpers'
import { useWebAppContext } from 'app/src/shared/hooks'
import successImage from 'app/static/images/success_img.png'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

function InProgressView(props: CardInProgressViewBaseProps) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher' })
  const context = useWebAppContext()

  const shoppingConfig: Required<portalConfigurationApi.ShoppingConfig['cardPolling']> = useMemo(() => {
    const def_cardPollingInterval = 1000
    const def_cardTakeInChargeMaxCount = 10
    if (!context.portalConfiguration.shoppingConfig?.cardPolling)
      return {
        cardPollingInterval: def_cardPollingInterval,
        cardTakeInChargeMaxCount: def_cardTakeInChargeMaxCount,
      }
    else
      return {
        cardPollingInterval:
          context.portalConfiguration.shoppingConfig.cardPolling.cardPollingInterval ?? def_cardPollingInterval,
        cardTakeInChargeMaxCount:
          context.portalConfiguration.shoppingConfig.cardPolling.cardTakeInChargeMaxCount ?? def_cardTakeInChargeMaxCount,
      }
  }, [context.portalConfiguration.shoppingConfig])

  useEffect(() => {
    cardPollingWorker(props.purchaseData, shoppingConfig, context).then(props.setPurchaseData)
  }, [context, props?.purchaseData, props?.setPurchaseData, shoppingConfig])

  return (
    <div className='mt-2 flex'>
      <div className='mx-auto'>
        <ProgressSpinner data-testid='loading-element' className='flex' />
        <div className='mt-4'>{t('requestInProgressDescription')}</div>
      </div>
    </div>
  )
}

function FailedView() {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher' })

  return (
    <ErrorPage errorMessage={t('requestInFailErrorMessage')}>
      <div className='mx-auto flex justify-center'>
        <BackToCatalogButton />
      </div>
    </ErrorPage>
  )
}

function TakeInChargeView() {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher' })

  return (
    <div className='mt-2 flex'>
      <div className='mx-auto'>
        <Image
          pt={{ image: { className: 'object-fit mx-auto h-[150px] lg:h-[250px]' } }}
          src={successImage}
          alt="Immagine che descrive la presa in carico dell'operazione"
        />
        <div className='mx-auto' dangerouslySetInnerHTML={{ __html: t('takeInChargeMessage') }} />
        <div className='mx-auto flex justify-center'>
          <BackToCatalogButton />
        </div>
      </div>
    </div>
  )
}

function CompletedView(props: CardInProgressViewBaseProps) {
  const orderListResp = shoppingApi.useOrderList({
    queryParams: {
      parentOrderId: props.purchaseData.order.orderId,
      status: ['confirmed', 'rejected'],
      includeUserInfo: true,
      includePurchaseDetails: true,
      pagingLimit: 50,
    },
  })

  if (
    orderListResp.data &&
    orderListResp.data.items.length > 0 &&
    orderListResp.data?.items.length ==
      (props.purchaseData.order?.purchaseDetails as shoppingApi.OrderDetailsPurchaseMultiPurchase)?.childrenCount
  )
    return (
      <div className='mt-2 flex'>
        <div className='mx-auto mt-4'>
          <MultiVoucherDetails parentOrder={props.purchaseData.order} orders={orderListResp.data?.items} funnelMode />
        </div>
      </div>
    )
  else return <TakeInChargeView />
}

function DraftView(props: CardInProgressViewBaseProps) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher' })

  const [showConfirm, setShowConfirm] = useState(false)
  const context = useWebAppContext()
  const orderStartMutation = shoppingApi.useOrderStart()
  const [showUsageModal, setShowUsageModal] = useState(false)

  return (
    <>
      {showConfirm && (
        <PortalConfirmDialog
          accept={async () => {
            try {
              await orderStartMutation.mutateAsync({ pathParams: { orderId: props.purchaseData.order.orderId } })
              props.setPurchaseData({ status: CardPurchaseStatus.INPROGRESS, order: props.purchaseData.order })
            } catch (err) {
              context.logger?.error('Error While Starting Order', { orderId: props.purchaseData.order.orderId, error: err })
              props.setPurchaseData({ status: CardPurchaseStatus.FAILED, order: props.purchaseData.order })
            } finally {
              setShowConfirm(false)
            }
          }}
          reject={() => setShowConfirm(false)}
          body={t('confirmDialog.body')}
          title={t('confirmDialog.title')}
        />
      )}

      <div className='m-4 mx-auto max-w-5xl'>
        <div className='card grid grid-cols-1 divide-x-0 divide-y-2 p-5 lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
          <MultiVoucherLeftPanelSide
            order={props.purchaseData.order}
            setShowUsageModal={setShowUsageModal}
            showUsageModal={showUsageModal}
            showBtnUsage={false}
          />
          <MultiVoucherRightPanelSide order={props.purchaseData.order} showCredit showFringeSpinner />
        </div>
        <div className='mt-4 grid grid-rows-2 rounded-md bg-[var(--card-background)] p-4'>
          <div>
            <h3>{t('detailDescription')}</h3>
          </div>
          <div className='grid grid-cols-2'>
            <div className='grid-rows-2'>
              <div>{t('buyerDescription')}</div>
              <div className='mt-2'>
                <h3>{BuildBeneficiaries(props.purchaseData.order)}</h3>
              </div>
            </div>
            <div className='grid-rows-2'>
              <div>{t('buyDateDescription')}</div>
              <div className='mt-2'>
                <h3>{formatDateDDMMYYYY(new Date(), true)}</h3>
              </div>
            </div>
          </div>
        </div>
        <div className='ml-2 mt-5'>
          <span className='text-[16px] font-bold leading-7'>{t('multivoucherSummaryTitle')}</span>
          <ul className='mt-2 list-disc pl-10'>
            {(props.purchaseData.order?.purchaseDetails as shoppingApi.OrderDetailsPurchaseMultiPurchase)?.variants?.map((v) => (
              <li key={v.variant.variantId}>
                <span className='text-[16px] font-bold leading-7'>{v.variant.variantName} - </span>
                <span className='text-[16px] font-normal leading-7'>
                  {t('multivoucherSummaryQnt')} {v.quantity}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className='m-6 text-center'>
        <Button rounded label={t('confirmButtonLabel')} onClick={() => setShowConfirm(true)} />
      </div>
    </>
  )
}

export function MultiVoucherFunnel(props: { order: shoppingApi.OrderDetails }) {
  const [purchaseData, setPurchaseData] = useState<CardPurchaseData>()
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher' })

  const title = useMemo(() => {
    switch (purchaseData?.status) {
      case CardPurchaseStatus.DRAFT:
        return t('almostDoneTitle')
      case CardPurchaseStatus.INPROGRESS:
        return t('requestInProgressTitle')
      default:
        return ''
    }
  }, [purchaseData, t])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [purchaseData?.status])

  useEffect(() => {
    setPurchaseData({ order: props.order, status: getCardPurchaseStatus(props.order) })
  }, [props.order])

  return (
    <>
      <h1 className='welfareportal-titlecenter'>{title}</h1>
      {purchaseData?.status === CardPurchaseStatus.DRAFT && (
        <DraftView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
      {purchaseData?.status === CardPurchaseStatus.INPROGRESS && (
        <InProgressView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
      {purchaseData?.status === CardPurchaseStatus.FAILED && <FailedView />}
      {purchaseData?.status === CardPurchaseStatus.TAKEINCHARGE && <TakeInChargeView />}
      {(purchaseData?.status === CardPurchaseStatus.COMPLETED || purchaseData?.status === CardPurchaseStatus.DONE) && (
        <CompletedView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
    </>
  )
}
